import React, { useCallback, useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import Main from "../../components/layout/Main";
import AlertDialog from "../../components/layout/app/AlertDialog";
import { UserService } from "../../services/UserService";
import { apiMessage, isTimeout } from "../../api/apiMessage";
import useAlive from "../../hooks/useAlive";
import { isAdmin } from "./paths";
import { modeLabel } from "./automation/constants";
import Switches from "./automation/Switches";
import PilotHomes from "./automation/PilotHomes";
import LegacyUsage from "./automation/LegacyUsage";
import Health from "./automation/Health";
import "../../assets/styles/app-admin.css";

const Card = ({ title, description, busy, children }) => (
  <section className="hx-adm-card" aria-busy={busy || undefined}>
    <h2 className="hx-adm-title">{title}</h2>
    {description ? <p className="hx-adm-text">{description}</p> : null}
    {children}
  </section>
);

const saveFailure = (error) =>
  isTimeout(error)
    ? "The server didn't answer in time. The change may still have been saved: reload the page to check."
    : apiMessage(error, "Couldn't save the automation settings. Please try again.");

// The new Automation system's health, switches and test homes (hashh-api-v2
// /automation), for the admin account. Every change that hands a type to or
// from the new engine asks first. The old system was retired on 14 Oct 2026,
// so there's nothing left to migrate.
function AutomationAdminPage() {
  const alive = useAlive();
  const [settings, setSettings] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  // Home names by id, as the Health summary gives them: the API has no way yet
  // for the admin account to look a home up by name.
  const [names, setNames] = useState(() => new Map());
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null);

  useEffect(() => {
    let current = true;
    setLoadError(null);
    UserService.getAutomationSettings()
      .then((result) => current && setSettings(result))
      .catch((error) => current && setLoadError(apiMessage(error, "Couldn't load the automation settings.")));
    return () => {
      current = false;
    };
  }, [attempt]);

  const nameOf = useCallback((id) => names.get(String(id).toLowerCase()) || null, [names]);
  const homeLabel = (id) => nameOf(id) ?? `home ${id}`;
  const learnNames = useCallback((homes) => {
    const known = homes.filter((home) => home.name && home.homeId);
    if (!known.length) return;
    setNames((current) => {
      const next = new Map(current);
      known.forEach((home) => next.set(String(home.homeId).toLowerCase(), home.name));
      return next;
    });
  }, []);

  // A confirmation that closes itself before doing what was asked.
  const ask = ({ onConfirm, ...rest }) =>
    setDialog({
      type: "confirmation",
      ...rest,
      onConfirm: () => {
        setDialog(null);
        onConfirm();
      },
    });
  const fail = (title, message) => setDialog({ type: "error", title, message });

  const save = async (patch) => {
    setSaving(true);
    try {
      const result = await UserService.updateAutomationSettings(patch);
      if (alive.current && result) setSettings(result);
    } catch (error) {
      if (alive.current) fail("Couldn't save", saveFailure(error));
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  if (loadError || !settings) {
    return (
      <Main header="stack" title="Automation" hideTabBar>
        <div className="hx-adm">
          {loadError ? (
            <div className="hx-empty" role="alert">
              {loadError}
              <button type="button" className="hx-btn" onClick={() => setAttempt(attempt + 1)}>
                Try again
              </button>
            </div>
          ) : (
            <div className="hx-adm-loading" aria-label="Loading">
              <div className="hx-skel" style={{ height: 260 }} />
              <div className="hx-skel" style={{ height: 420 }} />
            </div>
          )}
        </div>
      </Main>
    );
  }

  const pilots = settings.pilotHomes;
  const anyNew = Object.values(settings.types).includes("new");
  const where = settings.enabled
    ? "every home"
    : pilots.length
    ? pilots.length === 1
      ? "the test home"
      : `the ${pilots.length} test homes`
    : null;

  // A change from the switches. With Legacy and Shadow the new engine switches
  // nothing, so moving between them saves straight away; anything that hands a
  // type to or from the new engine, or turns Automation on for every home,
  // asks first. Turning it off for every home, and handing a retired type back,
  // the API refuses, so the switches don't offer them.
  const change = (field, value) => {
    if (field === "tagLinkBase") {
      save({ tagLinkBase: value });
    } else if (field === "notificationsEnabled") {
      if (!value) {
        save({ notificationsEnabled: false });
      } else {
        ask({
          title: "Turn on notifications",
          message: "Automations set to notify will send push notifications to the people they name.",
          confirmLabel: "Turn on",
          onConfirm: () => save({ notificationsEnabled: true }),
        });
      }
    } else if (field === "enabled" && value) {
      ask({
        title: "Turn on for every home",
        message: `Every home gets Automation${
          anyNew ? ", and types set to New run in every home straight away" : ""
        }. It can't be turned off again: the old system is retired. Saving can take a few seconds.`,
        confirmLabel: "Turn on",
        onConfirm: () => save({ enabled: true }),
      });
    } else if (field === "type") {
      const { key, label, mode } = value;
      const from = settings.types[key];
      const patch = { types: { [key]: mode } };
      if (from === mode) return;
      if (from !== "new" && mode !== "new") {
        save(patch);
      } else if (mode === "new") {
        ask({
          title: `Switch ${label} to New`,
          message: where
            ? from === "shadow"
              ? `The new engine switches devices for “${label}” in ${where}, instead of only recording dry runs.`
              : `The new engine runs “${label}” in ${where}, and the app offers it there.`
            : `Automation isn't on for any home yet, so this takes effect once a test home is added or it's turned on for every home.`,
          confirmLabel: "Switch to New",
          onConfirm: () => save(patch),
        });
      } else {
        ask({
          title: `Switch ${label} to ${modeLabel(mode)}`,
          message: `The new engine stops running “${label}”.${
            mode === "shadow"
              ? " It goes on recording dry runs, and the app still offers it."
              : ` The app stops offering it.${key === "cycling" ? " Devices a cycle has switched on are switched off." : ""}`
          }`,
          confirmLabel: `Switch to ${modeLabel(mode)}`,
          onConfirm: () => save(patch),
        });
      }
    }
  };

  const changePilot = (action, id) => {
    const name = homeLabel(id);
    if (action === "add") {
      ask({
        title: "Add a test home",
        message: settings.enabled
          ? `${name} becomes a test home. Automation is already on for it, because it's on for every home.`
          : `Automation turns on for ${name}.${anyNew ? " Types set to New run there straight away." : ""}`,
        confirmLabel: "Add",
        onConfirm: () => save({ pilotHomes: [...pilots, id] }),
      });
    } else {
      ask({
        title: "Remove a test home",
        message: settings.enabled
          ? `${name} stops being a test home. Automation stays on for it, because it's on for every home.`
          : `Automation turns off for ${name}.`,
        confirmLabel: "Remove",
        onConfirm: () => save({ pilotHomes: pilots.filter((item) => item !== id) }),
      });
    }
  };

  return (
    <Main header="stack" title="Automation" hideTabBar>
      <div className="hx-adm">
        <p className="hx-adm-intro">
          How the Automation system runs, and its switches. Only the admin account sees this page.
        </p>

        <Card
          title="Health"
          description="How the new engine's automations have been running, and what needs a look."
        >
          <Health onNames={learnNames} />
        </Card>

        <Card title="Switches" busy={saving}>
          {saving ? (
            <p className="hx-adm-saving" role="status">
              Saving… this can take a few seconds.
            </p>
          ) : null}
          <Switches settings={settings} disabled={saving} onChange={change} />
        </Card>

        <Card title="Test homes" busy={saving}>
          <PilotHomes pilotHomes={pilots} nameOf={nameOf} disabled={saving} onChange={changePilot} />
        </Card>

        <Card
          title="Old routes usage"
          description="Calls old apps still make to the retired routine, scene and rule routes, which answer “This has moved to Automations”. Once these stop, the old apps are gone."
        >
          <LegacyUsage />
        </Card>
      </div>

      <AlertDialog
        open={Boolean(dialog)}
        type={dialog?.type}
        title={dialog?.title}
        message={dialog?.message}
        confirmLabel={dialog?.confirmLabel}
        onClose={() => setDialog(null)}
        onConfirm={dialog?.onConfirm}
      />
    </Main>
  );
}

// Anyone but the admin account goes back to the dashboard.
const AutomationAdmin = () => (isAdmin() ? <AutomationAdminPage /> : <Redirect to="/dashboard" />);

export default AutomationAdmin;
